import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import type {
  LoomRebuildBlocker,
  LoomRebuildPhase,
  LoomRebuildStatus,
} from "../../api/loomRebuild";
import type { LoomAuthoritativeRebuildRuntime } from "./loomAuthoritativeRebuildScheduler";
import type { LoomVerifiedAircraftTransitionAsset } from "./loomAircraftTransitionAsset";
import { LoomJunctionContinuityReceipt } from "./LoomJunctionContinuityReceipt";

type LoomRebuildControlRuntime = LoomAuthoritativeRebuildRuntime & {
  settlingRevision?: number | null;
};

function unavailableSummary(detail: string, workerMissing: boolean): string {
  if (workerMissing) return "Unavailable · authoritative worker not configured";
  const constraints: string[] = [];
  if (/transform-wrapped/i.test(detail)) constraints.push("transform wrapper");
  const outOfRange = detail.match(/ is outside /g)?.length ?? 0;
  if (outOfRange) constraints.push(`${outOfRange} parameter${outOfRange === 1 ? "" : "s"} out of range`);
  return `Unavailable · ${constraints.join(" + ") || "unsupported Pattern definition"}`;
}

function firstConcreteReason(detail: string): string {
  const reasons = detail.replace(/^Not rebuildable:\s*/i, "").replace(/\.$/, "").split("; ");
  return reasons.find((reason) => / is outside /i.test(reason)) ?? reasons[0] ?? detail;
}

const UNSUPPORTED_GRAPH_USER_MESSAGE =
  "This assembly uses geometry the current update path does not support yet. The last accepted geometry will stay visible.";
const UNSUPPORTED_GRAPH_TECHNICAL_DETAIL =
  "The current aircraft occurrence rebuild accepts one connected fuselage with one symmetric physical wing pair. This graph is outside that bounded assembly class.";
const UNSUPPORTED_GRAPH_PREFIXES = [
  "accepted rebuild requires one fuselage",
  "the geometry engine could not build every wing and fuselage occurrence",
] as const;

function isUnsupportedGraphBlocker(blocker: LoomRebuildBlocker): boolean {
  const message = blocker.message.toLowerCase();
  return blocker.code === "graph-structure"
    && UNSUPPORTED_GRAPH_PREFIXES.some((prefix) => message.startsWith(prefix));
}

function visibleBlockerMessage(blocker: LoomRebuildBlocker): string {
  return isUnsupportedGraphBlocker(blocker) ? UNSUPPORTED_GRAPH_USER_MESSAGE : blocker.message;
}

function technicalDetailFor(blockers: readonly LoomRebuildBlocker[], detail: string): string {
  const unsupported = blockers.find(isUnsupportedGraphBlocker);
  return unsupported ? detail.replace(unsupported.message, UNSUPPORTED_GRAPH_TECHNICAL_DETAIL) : detail;
}

function firstBlocker(blockers: readonly LoomRebuildBlocker[], detail: string): string {
  const blocker = blockers.find((item) => item.code === "parameter-out-of-range") ?? blockers[0];
  return blocker ? visibleBlockerMessage(blocker) : firstConcreteReason(detail);
}

const PHASE_LABELS: Record<LoomRebuildPhase, string> = {
  preparing: "Preparing request",
  evaluating_parametric_surfaces: "Evaluating parametric surfaces",
  implicit_guided_trimming: "Implicit-guided trim",
  panelization_qc: "Panelization and QC",
  packing_verification: "Packing and verification",
  complete: "Worker complete",
};

function duration(milliseconds: number | null): string {
  if (milliseconds === null) return "none";
  return milliseconds < 1_000 ? `${Math.round(milliseconds)} ms`
    : `${(milliseconds / 1_000).toFixed(1)} s`;
}

export function LoomAuthoritativeRebuildControl({
  acceptedContinuityAsset, runtime, serviceStatus, onRebuild, onReviewBlockers,
}: {
  acceptedContinuityAsset?: LoomVerifiedAircraftTransitionAsset | null;
  runtime: LoomRebuildControlRuntime;
  serviceStatus: LoomRebuildStatus | null;
  onRebuild: () => void;
  onReviewBlockers?: (blockers: readonly LoomRebuildBlocker[]) => void;
}) {
  const [blockersExpanded, setBlockersExpanded] = useState(false);
  const [popoverPosition, setPopoverPosition] = useState({ right: 12, top: 12 });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLElement>(null);
  const unavailable = runtime.phase === "unsupported" || serviceStatus?.configured === false;
  const settling = !unavailable && !runtime.transportActive && runtime.settlingRevision != null;
  const queuedOnly = !runtime.transportActive && runtime.queuedRevision !== null;
  const blockerMessages = runtime.blockers.length
    ? runtime.blockers.map(visibleBlockerMessage)
    : [runtime.error ?? serviceStatus?.detail ?? "The authoritative worker is unavailable."];
  const label = unavailable
    ? runtime.blockers.length
      ? `Review ${runtime.blockers.length} geometry issue${runtime.blockers.length === 1 ? "" : "s"}`
      : "Review geometry setup"
    : settling ? "Update now"
    : queuedOnly ? "Queued…"
    : runtime.phase === "rebuilding" ? "Updating…"
    : runtime.phase === "error" ? "Retry update" : "Update geometry";
  const lastMeasuredBuildMs = runtime.lastMeasuredBuildMs ?? serviceStatus?.last_build_ms ?? null;
  const estimate = serviceStatus?.worker_state === "warm"
    ? serviceStatus.warm_rebuild_estimate_ms : serviceStatus?.cold_start_estimate_ms;
  const timing = runtime.result
    ? `accepted in ${duration(runtime.result.buildMs)}`
    : lastMeasuredBuildMs !== null ? `last measured ${duration(lastMeasuredBuildMs)}`
      : estimate === undefined ? "timing unavailable" : `estimated ${duration(estimate)}`;
  const rawDetail = runtime.error ?? (settling
    ? "The latest authoring change is waiting briefly for nearby edits. The last accepted aircraft remains visible."
    : runtime.phase === "ready"
    ? "The accepted aircraft surface is current. Occurrence ownership and union receipts are bound to this rebuild; aerodynamic panels and Jacobians remain separate."
    : serviceStatus?.detail ?? "Checking authoritative worker…");
  const detail = technicalDetailFor(runtime.blockers, rawDetail);
  const summary = unavailable
    ? unavailableSummary(rawDetail, serviceStatus?.configured === false)
    : settling ? "Changes settling · accepted aircraft retained"
    : queuedOnly ? "Latest rebuild queued · waiting for canceled transport to settle"
    : runtime.phase === "ready" ? "Aircraft surface accepted"
      : runtime.phase === "rebuilding" ? runtime.progress
        ? `Rebuilding · ${PHASE_LABELS[runtime.progress.phase]}`
        : "Rebuild submitted · awaiting request-bound worker status"
        : runtime.phase === "error" ? `Rebuild failed${runtime.failureStage
          ? ` at ${PHASE_LABELS[runtime.failureStage]}` : ""}`
          : "Aircraft occurrence rebuild";
  const technicalSummary = unavailable || runtime.phase === "error" ? summary : `${timing} · ${summary}`;
  const visibleStatus = unavailable ? "Geometry update unavailable"
    : settling ? "Waiting for edits to settle…"
    : queuedOnly ? "Geometry update queued"
    : runtime.phase === "ready" ? "Geometry up to date"
      : runtime.phase === "rebuilding" ? "Checking geometry…"
        : runtime.phase === "error" ? "Update failed"
          : serviceStatus === null ? "Checking geometry…" : "Geometry ready";
  const progressLabel = runtime.transportActive
    ? runtime.progress ? PHASE_LABELS[runtime.progress.phase] : "Awaiting worker status"
    : settling ? "Waiting for editing pause"
    : queuedOnly ? "Queued for rebuild"
    : runtime.phase === "error" ? runtime.failureStage
      ? `Failed at ${PHASE_LABELS[runtime.failureStage]}` : "Transport failed before a worker phase"
      : runtime.phase === "ready" ? "Complete"
        : runtime.phase === "unsupported" ? "Blocked before rebuild" : "Idle";
  const continuityAsset = acceptedContinuityAsset ?? (runtime.result?.assemblyAsset?.header.version === 3
    ? runtime.result.assemblyAsset as LoomVerifiedAircraftTransitionAsset : null);
  useEffect(() => {
    if (!blockersExpanded) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault(); event.stopPropagation();
      setBlockersExpanded(false);
      buttonRef.current?.focus({ preventScroll: true });
    };
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target;
      if (!(target instanceof Node) || buttonRef.current?.contains(target)
          || popoverRef.current?.contains(target)) return;
      setBlockersExpanded(false);
    };
    document.addEventListener("keydown", onKeyDown, true);
    document.addEventListener("pointerdown", onPointerDown, true);
    return () => {
      document.removeEventListener("keydown", onKeyDown, true);
      document.removeEventListener("pointerdown", onPointerDown, true);
    };
  }, [blockersExpanded]);
  const activate = () => {
    if (!unavailable) {
      onRebuild();
      return;
    }
    setBlockersExpanded((expanded) => {
      if (!expanded) {
        const bounds = buttonRef.current?.getBoundingClientRect();
        if (bounds) setPopoverPosition({
          right: Math.max(12, window.innerWidth - bounds.right),
          top: Math.min(bounds.bottom + 6, Math.max(12, window.innerHeight - 332)),
        });
        if (runtime.blockers.length) onReviewBlockers?.(runtime.blockers);
      }
      return !expanded;
    });
  };
  return <div className="loom-authoritative-rebuild" data-accepted-scope="aircraft-occurrence-union"
    data-action={unavailable ? "review-blockers" : "rebuild"}
    data-current-request-id={runtime.activeRequestId ?? "none"}
    data-current-revision={runtime.activeRevision ?? "none"}
    data-elapsed-ms={runtime.elapsedMs}
    data-failure-code={runtime.failureCode ?? "none"}
    data-failure-stage={runtime.failureStage ?? "none"}
    data-last-build-ms={lastMeasuredBuildMs ?? "none"}
    data-phase={runtime.phase}
    data-settling-revision={runtime.settlingRevision ?? "none"}
    data-queued-request-id={runtime.queuedRequestId ?? "none"}
    data-queued-revision={runtime.queuedRevision ?? "none"}
    data-transport-active={runtime.transportActive}
    data-worker-phase={runtime.progress?.phase ?? "none"}
    data-testid="loom-authoritative-rebuild">
    <button aria-controls={unavailable ? "loom-authoritative-rebuild-blockers" : undefined}
      aria-describedby="loom-authoritative-rebuild-status"
      aria-expanded={unavailable ? blockersExpanded : undefined} className="loom-presentation-reset"
      data-testid="loom-authoritative-rebuild-button" disabled={runtime.phase === "rebuilding" || queuedOnly}
      onClick={activate} ref={buttonRef} type="button">{label}</button>
    <span aria-atomic="true" aria-live="polite" data-testid="loom-authoritative-rebuild-status"
      id="loom-authoritative-rebuild-status" role="status">
      <span data-testid="loom-authoritative-rebuild-summary">{visibleStatus}</span>
    </span>
    <details className="loom-authoritative-rebuild-technical"
      data-testid="loom-authoritative-rebuild-technical">
      <summary>Technical details</summary>
      <div className="loom-authoritative-rebuild-technical-copy">
        <span data-testid="loom-authoritative-rebuild-technical-summary">{technicalSummary}</span>
        <span data-testid="loom-authoritative-rebuild-technical-detail">{detail}</span>
        {unavailable ? <span className="loom-authoritative-rebuild-detail"
          data-testid="loom-authoritative-rebuild-detail">{firstBlocker(runtime.blockers, detail)}</span> : null}
      </div>
      <section aria-label="Geometry update progress"
        className="loom-authoritative-rebuild-progress"
        data-testid="loom-authoritative-rebuild-progress">
        <div>
          <strong data-testid="loom-authoritative-rebuild-progress-phase">{progressLabel}</strong>
          <time aria-label={`${duration(runtime.elapsedMs)} elapsed`}
            data-testid="loom-authoritative-rebuild-progress-elapsed">
            {duration(runtime.elapsedMs)} elapsed
          </time>
        </div>
        <dl>
          <div><dt>Current</dt><dd data-testid="loom-authoritative-rebuild-progress-current">
            {runtime.activeRevision === null ? "none"
              : `r${runtime.activeRevision} · loom-rebuild-${runtime.activeRequestId}`}
          </dd></div>
          <div><dt>Queued</dt><dd data-testid="loom-authoritative-rebuild-progress-queued">
            {runtime.queuedRevision === null ? "none"
              : `r${runtime.queuedRevision} · loom-rebuild-${runtime.queuedRequestId}`}
          </dd></div>
          <div><dt>Last</dt><dd data-testid="loom-authoritative-rebuild-last-duration">
            {duration(lastMeasuredBuildMs)}
          </dd></div>
        </dl>
        {runtime.progress ? <p data-testid="loom-authoritative-rebuild-progress-message">
          {runtime.progress.message}
          {runtime.progress.candidate_index === null ? "" :
            ` · candidate ${runtime.progress.candidate_index}/${runtime.progress.candidate_count}`}
        </p> : null}
        {runtime.phase === "error" ? <div className="loom-authoritative-rebuild-failure">
          <strong data-testid="loom-authoritative-rebuild-failure-stage">{progressLabel}</strong>
          <p data-testid="loom-authoritative-rebuild-failure-reason">{runtime.error}</p>
        </div> : null}
      </section>
      <LoomJunctionContinuityReceipt asset={continuityAsset} />
    </details>
    {unavailable && blockersExpanded ? createPortal(
      <aside aria-label="Geometry update issues" className="loom-authoritative-rebuild-popover"
        data-testid="loom-authoritative-rebuild-blockers" id="loom-authoritative-rebuild-blockers"
        ref={popoverRef} style={popoverPosition}>
        <header><strong>Geometry update issues</strong><span>No geometry changed</span></header>
        <span className="loom-authoritative-rebuild-blocker-list" role="list">
          {blockerMessages.map((message, index) => <span data-testid="loom-authoritative-rebuild-blocker"
            key={`${index}:${message}`} role="listitem">{message}</span>)}
        </span>
      </aside>, document.body,
    ) : null}
  </div>;
}
